import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Home, LogOut, Trophy, Award } from 'lucide-react';
import ChallengeCard from '@/components/ChallengeCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Challenges({ user, onLogout }) {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [completedToday, setCompletedToday] = useState(0);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/challenges/daily`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallenges(response.data.challenges);
      setCompletedToday(response.data.completed_today);
    } catch (error) {
      toast.error('Görevler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (challengeId) => {
    setCompleting(challengeId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/challenges/${challengeId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`🎉 ${response.data.message} +${response.data.xp_earned} XP!`);
      fetchChallenges();
    } catch (error) {
      const message = error.response?.data?.message || 'Bir hata oluştu';
      toast.error(message);
    } finally {
      setCompleting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="animate-pulse text-lg">Yükleniyor...</div>
      </div>
    );
  }

  const totalChallenges = challenges.length;
  const progressPercentage = totalChallenges > 0 ? (completedToday / totalChallenges) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🎯</span>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Günlük Görevler</h1>
              <p className="text-sm text-gray-600">{completedToday}/{totalChallenges} tamamlandı</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="home-btn"
              variant="outline"
              onClick={() => navigate('/')}
              className="gap-2 hover-lift"
            >
              <Home className="w-4 h-4" />
              Ana Sayfa
            </Button>
            <Button
              data-testid="logout-btn"
              variant="outline"
              onClick={onLogout}
              className="gap-2 hover-lift"
            >
              <LogOut className="w-4 h-4" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Overview */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-6 animate-scale-in">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Bugünkü İlerleme</h3>
              <p className="text-sm text-gray-600">Her görev XP kazandırır!</p>
            </div>
            <div className="text-4xl">
              {completedToday === totalChallenges ? '🎉' : '💪'}
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full progress-bar flex items-center justify-center text-white text-sm font-semibold"
              style={{ width: `${progressPercentage}%` }}
            >
              {progressPercentage > 10 && `${Math.round(progressPercentage)}%`}
            </div>
          </div>
        </div>

        {/* Challenges List */}
        <div className="space-y-4">
          {challenges.map((challenge, index) => (
            <div
              key={challenge.id}
              className="animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <ChallengeCard
                challenge={challenge}
                onComplete={handleComplete}
                loading={completing === challenge.id}
              />
            </div>
          ))}
        </div>

        {/* Motivational Message */}
        {completedToday === totalChallenges && (
          <div className="mt-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 text-center animate-scale-in">
            <Trophy className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">🎉 Tebrikler!</h3>
            <p className="text-gray-700">Tüm günlük görevleri tamamladın! Yarın yeni görevler seni bekliyor.</p>
          </div>
        )}
      </main>
    </div>
  );
}